import { useGetAdminPayerByIdQuery, useGetPayerByIdQuery } from '@features/payer-search/store/api';
import { Button, Stack, Typography } from '@mui/material';

/** Staff reads use staff authorization and do not require clinic membership. */
export function AdminCredentialingPayerName({ id }: { id: string }) {
  const { currentData, isFetching, refetch } = useGetAdminPayerByIdQuery({ payerId: id });
  return (
    <PayerName
      name={currentData?.display_name}
      payerId={currentData?.primary_payer_id}
      pending={isFetching}
      onRetry={() => void refetch()}
    />
  );
}

/** RTK deduplicates repeated payer IDs across rows; each page has at most 25 distinct lookups. */
export function CredentialingPayerName({ id }: { id: string }) {
  const { currentData, isFetching, refetch } = useGetPayerByIdQuery({ payerId: id });
  return (
    <PayerName
      name={currentData?.display_name}
      payerId={currentData?.primary_payer_id}
      pending={isFetching}
      onRetry={() => void refetch()}
    />
  );
}

/** The payer name with its directory Payer ID beneath; no ID renders nothing, never a substitute. */
export function CredentialingPayerLabel({
  name,
  payerId,
}: {
  name: string;
  payerId?: string | null;
}) {
  return (
    <>
      {name}
      {payerId && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Payer ID {payerId}
        </Typography>
      )}
    </>
  );
}

function PayerName({
  name,
  payerId,
  pending,
  onRetry,
}: {
  name?: string;
  payerId?: string | null;
  pending: boolean;
  onRetry: () => void;
}) {
  if (name) return <CredentialingPayerLabel name={name} payerId={payerId} />;
  if (pending) return <>Loading payer…</>;
  return (
    <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
      <Typography variant="body2">Payer name unavailable</Typography>
      <Button size="small" onClick={onRetry}>
        Retry payer name
      </Button>
    </Stack>
  );
}
